/**
 * Scenario History Panel Component
 * Slide-out panel showing evaluation history for a single scenario
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useI18n } from '@/i18n';
import { useScenarioResults } from '../hooks/useEvalScenarios';
import { ScoreTrendChart } from './ScoreTrendChart';
import { EvalResultDetailDialog } from './EvalResultDetailDialog';
import type { EvalResultDetail, MetricResult } from '@/types/evalScenarios';

interface ScenarioHistoryPanelProps {
  scenarioId: string;
  scenarioName: string;
  isOpen: boolean;
  onClose: () => void;
}

export function ScenarioHistoryPanel({
  scenarioId,
  scenarioName,
  isOpen,
  onClose,
}: ScenarioHistoryPanelProps) {
  const { messages, language } = useI18n();
  const t = messages.evalScenarios;
  const tc = messages.common;

  const { data, isLoading, error, refetch } = useScenarioResults(
    isOpen ? scenarioId : null,
    20
  );

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedResult, setSelectedResult] = useState<EvalResultDetail | null>(null);

  // Lock body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Reset state when panel closes
  useEffect(() => {
    if (!isOpen) {
      setExpandedId(null);
      setSelectedResult(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const results = data?.results || [];

  // Prepare data for trend chart (only completed evaluations with scores)
  const chartData = results
    .filter((r) => r.status !== 'running' && r.overall_score != null && r.passed != null)
    .map((r) => ({
      score: (r.overall_score as number) * 100,
      passed: r.passed as boolean,
      date: r.executed_at,
    }));

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(language === 'zh' ? 'zh-CN' : 'en-US', {
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    const timeTexts = t.time;
    if (diffMins < 1) return timeTexts?.justNow || 'Just now';
    if (diffMins < 60) return (timeTexts?.minutesAgo || '{n} minutes ago').replace('{n}', String(diffMins));
    if (diffHours < 24) return (timeTexts?.hoursAgo || '{n} hours ago').replace('{n}', String(diffHours));
    if (diffDays < 7) return (timeTexts?.daysAgo || '{n} days ago').replace('{n}', String(diffDays));
    return formatDate(dateString);
  };

  const renderMetricBadge = (metric: MetricResult) => {
    const score = (metric.score * 100).toFixed(0);
    const threshold = (metric.threshold * 100).toFixed(0);
    const passed = metric.passed;

    return (
      <div
        key={metric.name}
        className={`flex items-center justify-between px-2 py-1 rounded text-xs ${
          passed ? 'bg-green-50' : 'bg-red-50'
        }`}
      >
        <span className="text-gray-700">{metric.name}</span>
        <span className={passed ? 'text-green-700' : 'text-red-700'}>
          {score}% / {threshold}%
        </span>
      </div>
    );
  };

  const renderResultItem = (result: EvalResultDetail) => {
    const isExpanded = expandedId === result.id;
    const isRunning = result.status === 'running';
    const isFailed = result.status === 'failed';
    const isPending = isRunning || isFailed;
    const score = result.overall_score != null ? (result.overall_score * 100).toFixed(1) : null;

    // Determine border and background colors based on status
    const getBorderClass = () => {
      if (isRunning) return 'border-blue-300 bg-blue-50';
      if (isFailed) return 'border-red-300 bg-red-50';
      return 'border-gray-200';
    };

    const getHeaderClass = () => {
      if (isRunning) return 'bg-blue-50 hover:bg-blue-100';
      if (isFailed) return 'bg-red-50 hover:bg-red-100';
      return 'bg-gray-50 hover:bg-gray-100';
    };

    return (
      <div
        key={result.id}
        className={`border rounded-lg overflow-hidden ${getBorderClass()}`}
      >
        {/* Header - always visible */}
        <div
          className={`p-3 cursor-pointer transition-colors ${getHeaderClass()}`}
          onClick={() => !isRunning && setExpandedId(isExpanded ? null : result.id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isRunning ? (
                <>
                  <span className="text-lg animate-spin">🔄</span>
                  <span className="font-medium text-blue-700">
                    {t.historyPanel?.running || 'Running...'}
                  </span>
                </>
              ) : isFailed ? (
                <>
                  <span className="text-lg">💥</span>
                  <span className="font-medium text-red-700">
                    {t.historyPanel?.failed || 'Failed'}
                  </span>
                </>
              ) : (
                <>
                  <span className="text-lg">
                    {result.passed ? '✅' : '❌'}
                  </span>
                  <span className={`font-medium ${result.passed ? 'text-green-700' : 'text-red-700'}`}>
                    {score}%
                  </span>
                </>
              )}
              {result.model_id && (
                <span className="text-xs bg-gray-200 px-1.5 py-0.5 rounded">
                  {result.model_id}
                </span>
              )}
              {!isPending && result.response_time_ms && (
                <span className="text-xs text-gray-500">
                  {(result.response_time_ms / 1000).toFixed(1)}s
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">
                {formatRelativeTime(result.executed_at)}
              </span>
              {!isRunning && (
                <span className="text-gray-400">
                  {isExpanded ? '▼' : '▶'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Expanded content - show for failed (to see error) but not for running */}
        {isExpanded && !isRunning && (
          <div className="p-3 space-y-3 border-t border-gray-200">
            {/* Metrics */}
            {result.metrics && result.metrics.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs font-medium text-gray-600 mb-1">
                  {t.historyPanel?.metrics || 'Metrics'}
                </div>
                <div className="grid grid-cols-2 gap-1">
                  {result.metrics.map(renderMetricBadge)}
                </div>
              </div>
            )}

            {/* Agent Response */}
            {result.response && (
              <div className="space-y-1">
                <div className="text-xs font-medium text-gray-600">
                  {t.historyPanel?.agentResponse || 'Agent Response'}
                </div>
                <div className="bg-gray-50 rounded p-2 text-sm text-gray-700 max-h-32 overflow-y-auto">
                  {result.response}
                </div>
              </div>
            )}

            {/* Error message */}
            {result.error && (
              <div className="bg-red-50 border border-red-200 rounded p-2">
                <div className="text-xs font-medium text-red-700 mb-1">
                  {tc.error}
                </div>
                <div className="text-sm text-red-600">{result.error}</div>
              </div>
            )}

            {/* Metadata and View Details */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span>
                  {t.historyPanel?.executedAt || 'Executed At'}: {formatDate(result.executed_at)}
                </span>
                {result.executed_by && (
                  <span>
                    {t.historyPanel?.executedBy || 'Executed By'}: {result.executed_by}
                  </span>
                )}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedResult(result);
                }}
                className="text-xs px-2 py-1 bg-primary-100 text-primary-700 rounded hover:bg-primary-200 transition-colors"
              >
                {t.resultDetail?.viewDetails || 'View Details'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />

      {/* Slide-out Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-xl bg-white shadow-xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <span className="text-lg">📊</span>
            <div>
              <h3 className="font-medium text-gray-900">
                {t.historyPanel?.title || 'Evaluation History'}
              </h3>
              <p className="text-sm text-gray-500 truncate max-w-xs">
                {scenarioName}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
            >
              {isLoading ? tc.loading : tc.refresh}
            </Button>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-200 rounded transition-colors"
            >
              <span className="text-xl text-gray-500">×</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-16 bg-gray-200 rounded" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-600 text-sm">
                {tc.error}: {error instanceof Error ? error.message : 'Unknown error'}
              </div>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-2">📭</div>
              <div className="text-gray-500">
                {t.historyPanel?.noHistory || 'No evaluation records'}
              </div>
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                <p className="flex items-center justify-center gap-1">
                  <span>💡</span>
                  {t.historyPanel?.checkRunsPanelHint || 'If you just started an evaluation, click the "Runs" button at the top to check progress'}
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Trend Chart */}
              <Card>
                <div className="p-4">
                  <ScoreTrendChart data={chartData} maxItems={10} />
                </div>
              </Card>

              {/* Results List */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700">
                  {t.historyPanel?.resultsList || 'Evaluation Records'} ({results.length})
                </div>
                {results.map(renderResultItem)}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Result Detail Dialog */}
      <EvalResultDetailDialog
        result={selectedResult}
        isOpen={!!selectedResult}
        onClose={() => setSelectedResult(null)}
      />
    </>
  );
}
